import { all, call, put, select, takeLatest } from "redux-saga/effects";
import queryString from "query-string";
import { normalize, schema } from "normalizr";
import papaparse from "papaparse";
import { toast } from "react-toastify";

import { getErrorMessage } from "../../../../utils/sagasErrorHandler";
import { saveStringAsFile } from "../../../../utils/file/file";
import { getObjectStructuresRequest } from "../../../ObjectStructures/services/Entities/api";
import { normalizersSelector } from "../../../../services/Normalizers/selectors";
import { branchesSelector } from "../../../../services/Branch/selectors";
import { getPublishRequestsCountRequest } from "../../../ConfigCI/services/ConfigCITests/api";
import { CONFIG_MANAGER_ITEMS_PER_PAGE } from "../../constants";
import messageHelper from "../../../../utils/messages/messageHelper";

import { configManagerSelector, configsSelector } from "./selectors";
import {
    deleteConfigRequest,
    getConfigsRequest,
    patchConfigRequest,
    publishRequestRequest,
    scheduleCITestsRequest
} from "./api";
import {
    getConfigsEntities,
    getConfigsEntitiesSuccess,
    getConfigsEntitiesError,
    changeWorkers,
    mergeWorkersDialog,
    setWorkersDialog,
    enabledConfig,
    enabledConfigError,
    enabledCheckedConfig,
    enabledCheckedConfigError,
    deleteConfig,
    deleteConfigError,
    exportScheme,
    publishRequest,
    setPublishRequestDialog,
    mergePublishRequestDialog,
    scheduleCITests
} from "./actions";
import { readPlatformFieldsByConfig } from "./helpers";

const configsSchema = new schema.Entity("configs");
const configsListSchema = new schema.Array(configsSchema);

//Workers
function* handleRefreshConfigs() {
    const { search } = window.location;
    yield put(getConfigsEntities({ limit: CONFIG_MANAGER_ITEMS_PER_PAGE, ...queryString.parse(search) }));
}

function* handleGetConfigsEntities ({ payload }) {
    try {
        const { configs, count } = yield call(getConfigsRequest, payload);
        const { result, entities } = normalize(configs, configsListSchema);

        const publishRequestsCount = yield all(configs.map(({
            branch_code,
            receiver_code,
            id,
            initial_source_code
        }) => call(getPublishRequestsCountRequest, {
            id,
            branch_code,
            receiver_code,
            initial_source_code,
            status_code: "NEW"
        })));

        let dataCount = {};
        publishRequestsCount.forEach((item) => dataCount = { ...dataCount, ...item });

        yield put(getConfigsEntitiesSuccess({
            entities: entities.configs,
            ids: result,
            publishRequestsCount: dataCount,
            count
        }));
    } catch (error) {
        yield put(getConfigsEntitiesError());
        getErrorMessage(error);
    }
}

function* handleChangeWorkers ({ payload }) {
    try {
        yield put(mergeWorkersDialog({ isLoading: true }));

        yield call(patchConfigRequest, payload);

        yield call(
            toast.success,
            `The Config with id - ${payload.id} has been successfully changed`
        );

        yield put(setWorkersDialog());
        yield handleRefreshConfigs();
    } catch (error) {
        yield put(mergeWorkersDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleEnabledWorker ({ payload }) {
    try {
        yield call(patchConfigRequest, payload);
        yield handleRefreshConfigs();
    } catch (error) {
        yield put(enabledConfigError());
        getErrorMessage(error);
    }
}

function* handleEnabledCheckedConfig ({ payload }) {
    try {
        const selectedConfigs = yield select(configsSelector);

        yield all(selectedConfigs.map((id) => call(
            patchConfigRequest, { id, body: { enabled: payload } }
        )));
        yield call(
            toast.success,
            `The Config${selectedConfigs.length > 1 ? "s" : ""} with id - 
            ${selectedConfigs.join(", ")} has been successfully ${payload ? "enabled" : "disabled"}`
        );
        yield handleRefreshConfigs();
    } catch (error) {
        yield put(enabledCheckedConfigError());
        getErrorMessage(error);
    }
}

function* handleDeleteConfig ({ payload }) {
    try {
        const selectedConfigs = yield select(configsSelector);
        let data = [ ...selectedConfigs ];

        if(payload) {
            data = Array.isArray(payload) ? payload : [ payload ];
        }

        yield all(data.map((id) => call(deleteConfigRequest, id)));

        messageHelper({
            type: "success",
            actionSubject: `Config${data.length > 1 ? "s" : ""} with id${data.length > 1 ? "s" : ""} - ${data.join(", ")}`,
            action: "deleted",
            plural: data.length > 1
        });
        yield handleRefreshConfigs();
    } catch (error) {
        yield put(deleteConfigError());
        getErrorMessage(error);
    }
}

function* handleExportScheme () {
    try {
        const { normalizers } = yield select(normalizersSelector);
        const { branches } = yield select(branchesSelector);
        const filesNames = [];
        const { search } = window.location;
        const params = {...queryString.parse(search), limit: 100000, offset: 0};
        if (params.initial_source_code === "_") {
            params.initial_source_code = "";
        }
        const { configs } = yield call(getConfigsRequest, params);

        const receiverCodesConfigs = configs.reduce((acc, config)=>{
            const newReceiverCodes = { ...acc };

            if(!newReceiverCodes[config.receiver_code]){
                newReceiverCodes[config.receiver_code] = [ config ];
            }else{
                newReceiverCodes[config.receiver_code].push(config);
            }

            return newReceiverCodes;
        }, {});

        const objectStructures = yield all(Object.keys(receiverCodesConfigs).map((receiver_code)=>(
            call(getObjectStructuresRequest, { receiver_code })
        )));

        const propertiesPerReceiverAndRO = {};
        objectStructures.forEach((objectStructureResponse) => {
            objectStructureResponse.object_structures.forEach((os) => {
                if(!propertiesPerReceiverAndRO[os.receiver_code]){
                    propertiesPerReceiverAndRO[os.receiver_code] = {};
                }
                if(!propertiesPerReceiverAndRO[os.receiver_code][os.code]){
                    propertiesPerReceiverAndRO[os.receiver_code][os.code] = [];
                }
                propertiesPerReceiverAndRO[os.receiver_code][os.code] = os.properties;
            })
        })

        const filesData = yield all(
            Object.keys(receiverCodesConfigs).flatMap((keyReceiverCode) => {
                return Object.keys(propertiesPerReceiverAndRO[keyReceiverCode] || {}).map((objectCode) => {
                    filesNames.push(
                        `scheme_parsed_fields_per_platform_${keyReceiverCode.toLowerCase()}_${objectCode.toLowerCase()}.csv`
                    );

                    const preparedData = receiverCodesConfigs[keyReceiverCode].map((config, index) => {
                        const data = {};
                        data[""] = index + 1;
                        data.Branch = branches[config.branch_code]?.name || config.branch_code;

                        const platformFieldsByConfig = readPlatformFieldsByConfig(config);

                        propertiesPerReceiverAndRO[keyReceiverCode][objectCode].forEach(({ name, code }) => {
                            let normalizeConfig = [];
                            const boxContent = platformFieldsByConfig[code] ? "+" : "-";

                            if (platformFieldsByConfig[code]?.length) {
                                platformFieldsByConfig[code].forEach((normalizerCode) => {
                                    if (normalizers[normalizerCode].name) {
                                        normalizeConfig.push(normalizers[normalizerCode].name);
                                    }
                                });
                            }

                            data[name] = normalizeConfig.length
                                ? `${boxContent} (${normalizeConfig.join("|")})`
                                : boxContent;
                        });

                        return data;
                    });

                    return call(papaparse.unparse, preparedData);
                });
            })
        );

        yield all(filesData.map((body, index) => (
            call(saveStringAsFile, body, filesNames[index])
        )));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handlePublishRequest ({ payload }) {
    try {
        yield put(mergePublishRequestDialog({ isLoading: true }));

        const { id, reviewers_suggestion } = payload;
        const { entities } = yield select(configManagerSelector);
        const { branch_code, modified_at_timestamp, receiver_code, initial_source_code } = entities[id];

        const data = {
            config_version: modified_at_timestamp,
            branch_code,
            receiver_code,
            reviewers_suggestion,
            initial_source_code
        };

        yield call(publishRequestRequest, data);

        yield put(setPublishRequestDialog());
        yield call(toast.success, `Publish request for branch ${branch_code} was successfully scheduled`);

        yield handleRefreshConfigs();
    } catch (error) {
        yield put(mergePublishRequestDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleScheduleCITests ({ payload }) {
    try {
        const { entities } = yield select(configManagerSelector);
        const { branch_code, modified_at_timestamp, receiver_code } = entities[payload];

        const data = { branch_code, config_version: modified_at_timestamp, receiver_code };

        yield call(scheduleCITestsRequest, data);
        yield call(toast.success, `Config CI tests for branch ${branch_code} was successfully scheduled`);
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* configManagerEntriesSagas() {
    yield all([
        yield takeLatest(getConfigsEntities, handleGetConfigsEntities),
        yield takeLatest(changeWorkers, handleChangeWorkers),
        yield takeLatest(enabledConfig, handleEnabledWorker),
        yield takeLatest(enabledCheckedConfig, handleEnabledCheckedConfig),
        yield takeLatest(deleteConfig, handleDeleteConfig),
        yield takeLatest(exportScheme, handleExportScheme),
        yield takeLatest(publishRequest, handlePublishRequest),
        yield takeLatest(scheduleCITests, handleScheduleCITests)
    ]);
}
